import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUrlFilters } from '../../hooks/useUrlFilters'
import {
  BarChart3, TrendingUp, Users, Target, DollarSign, RefreshCw,
  ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Phone, Mail, CheckSquare,
  PhoneCall, ChevronDown, ChevronUp,
} from 'lucide-react'
import { reportsApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import { fmtWeekday, fmtDayMonth } from '../../utils/date'
import { Select } from '../../components/ui/Input'
import Button from '../../components/ui/Button'
import LoadingState from '../../components/ui/LoadingState'
import MobileFilterPanel from '../../components/ui/MobileFilterPanel'
import { usePipelineStages, useScoringSegments } from '../../hooks/usePipelineStages'

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
]

const STAGE_COLORS_FALLBACK: Record<string, string> = {
  new: '#3b82f6',
  contacted: '#0ea5e9',
  qualified: '#6366f1',
  demo_scheduled: '#8b5cf6',
  proposal_sent: '#a855f7',
  negotiation: '#d946ef',
  won: '#10b981',
  lost: '#f87171',
}

function formatStage(stage: string): string {
  return stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/* ─── Top-level stat card with colored icon ─── */
function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-2 sm:mb-3 h-6">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon size={13} className="text-white" />
        </div>
        <span className="text-[11px] sm:text-xs text-gray-500 leading-tight">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-gray-900 text-center">{value}</p>
      {sub && <p className="text-[11px] sm:text-xs text-gray-500 mt-1 text-center">{sub}</p>}
    </div>
  )
}

/* ─── Section card wrapper ─── */
function SectionCard({ title, icon: Icon, children, className = '' }: {
  title: string; icon: any; children: React.ReactNode; className?: string
}) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 sm:p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-5">
        <Icon size={18} className="text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  )
}

function fmtINR(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const VOIP_PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
]

export default function ReportsPage() {
  const [filters, setFilter] = useUrlFilters({ period: 'last_30_days', voip_period: 'last_30_days', date_from: '', date_to: '' })
  const period = filters.period
  const setPeriod = (v: string) => setFilter('period', v)

  // For custom range, build a period string the backend can parse
  const effectivePeriod = (() => {
    if (period === 'custom' && filters.date_from && filters.date_to) {
      return `custom:${filters.date_from}:${filters.date_to}`
    }
    return period
  })()

  const authUser = useAuthStore((s) => s.user)
  const isAdminOrManager = authUser?.role === 'admin' || authUser?.role === 'manager'
  const voipPeriod = filters.voip_period
  const setVoipPeriod = (v: string) => setFilter('voip_period', v)
  const [voipOpen, setVoipOpen] = useState(true)
  const [agentSort, setAgentSort] = useState<{ col: string; asc: boolean }>({ col: 'total', asc: false })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reports-dashboard', effectivePeriod],
    queryFn: () => reportsApi.dashboard(effectivePeriod),
  })

  const { data: voipData, isLoading: voipLoading } = useQuery({
    queryKey: ['voip-costs-report', voipPeriod],
    queryFn: () => reportsApi.voipCosts(voipPeriod),
    enabled: isAdminOrManager,
  })

  const pipelineStages = usePipelineStages()
  const scoringSegments = useScoringSegments()

  // Build dynamic stage color map from API, falling back to hardcoded values
  const stageColorMap = useMemo(() => {
    if (pipelineStages.length > 0) {
      return pipelineStages.reduce<Record<string, string>>((acc, s) => {
        if (s.color) acc[s.slug] = s.color
        return acc
      }, {})
    }
    return STAGE_COLORS_FALLBACK
  }, [pipelineStages])

  // Build dynamic segment config from API, falling back to hardcoded defaults
  const segmentConfig = useMemo(() => {
    if (scoringSegments.length > 0) {
      return scoringSegments.map((seg) => ({
        key: seg.segment,
        label: seg.label,
        color: seg.color || '#6b7280',
        min_score: seg.min_score,
      }))
    }
    return [
      { key: 'HOT', label: 'Hot', color: '#ef4444', min_score: 75 },
      { key: 'WARM', label: 'Warm', color: '#f59e0b', min_score: 45 },
      { key: 'COLD', label: 'Cold', color: '#3b82f6', min_score: 0 },
    ]
  }, [scoringSegments])

  // Helper to get gauge color from score based on dynamic segments
  const getScoreColor = (score: number): string => {
    for (const seg of segmentConfig) {
      if (score >= seg.min_score) return seg.color
    }
    return segmentConfig[segmentConfig.length - 1]?.color || '#6b7280'
  }

  const d = (data?.data || {}) as any
  const pipeline = d.pipeline || {}
  const activity = d.activity || {}
  const sources = d.lead_sources || {}
  const scores = d.score_distribution || {}
  const revenue = d.revenue || {}
  const funnel: { stage: string; count: number }[] = pipeline.funnel || []
  const maxFunnel = Math.max(...funnel.map((f: any) => f.count), 1)
  const conversions = pipeline.conversion_rates || {}

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Performance insights across your pipeline</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select options={PERIODS} value={period} onChange={(e) => setPeriod(e.target.value)} />
          {period === 'custom' && (
            <MobileFilterPanel activeCount={(filters.date_from ? 1 : 0) + (filters.date_to ? 1 : 0)}>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => setFilter('date_from', e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) => setFilter('date_to', e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </MobileFilterPanel>
          )}
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingState message="Loading reports..." />
      ) : (
        <div className="space-y-6">
          {/* ── Top Stats ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard icon={Target} label="Total Leads" value={pipeline.total_leads || 0} color="bg-blue-500" />
            <StatCard icon={TrendingUp} label="Win Rate" value={`${pipeline.win_rate || 0}%`} color="bg-green-500" />
            <StatCard icon={Users} label="Won Deals" value={pipeline.won || 0} color="bg-emerald-500" />
            <StatCard icon={DollarSign} label="Revenue" value={`₹${(revenue.total_revenue || 0).toLocaleString()}`} color="bg-purple-500" />
            <StatCard icon={CheckSquare} label="Activities" value={activity.total_activities || 0} sub={PERIODS.find(p => p.value === period)?.label} color="bg-amber-500" />
            <StatCard icon={Phone} label={`${segmentConfig[0]?.label || 'Hot'} Leads`} value={scores.segments?.[segmentConfig[0]?.key || 'HOT'] || 0} color="bg-red-500" />
          </div>

          {/* ── Pipeline Funnel ── */}
          <SectionCard title="Pipeline Funnel" icon={BarChart3}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
              <p className="text-sm text-gray-500">{pipeline.total_leads || 0} leads across {funnel.length} stages</p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-gray-600">Won: <span className="font-semibold text-emerald-700">{pipeline.won || 0}</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-gray-600">Lost: <span className="font-semibold text-red-700">{pipeline.lost || 0}</span></span>
                </div>
              </div>
            </div>

            {funnel.length > 0 ? (
              <div className="space-y-3 mb-6">
                {funnel.map((s: any) => {
                  const pct = maxFunnel > 0 ? (s.count / maxFunnel) * 100 : 0
                  const color = stageColorMap[s.stage] || '#9ca3af'
                  return (
                    <div key={s.stage} className="flex items-center gap-3">
                      <div className="w-28 text-right">
                        <span className="text-xs font-medium text-gray-600">{formatStage(s.stage)}</span>
                      </div>
                      <div className="flex-1 h-8 bg-gray-50 rounded-lg overflow-hidden">
                        <div
                          className="h-full rounded-lg flex items-center px-3 transition-all duration-500"
                          style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: color }}
                        >
                          {pct > 15 && <span className="text-xs font-bold text-white">{s.count}</span>}
                        </div>
                      </div>
                      {pct <= 15 && <span className="text-xs font-semibold text-gray-700 w-8">{s.count}</span>}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No pipeline data yet — add leads to see your funnel</p>
            )}

            {/* Conversion rate grid */}
            {Object.keys(conversions).length > 0 && (
              <div className="pt-5 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Stage-to-Stage Conversion</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(conversions).map(([key, val]: [string, any]) => {
                    const parts = key.split('_to_')
                    const from = formatStage(parts[0] || '')
                    const to = formatStage(parts[1] || '')
                    const rateColor = val.rate > 50 ? 'text-green-600' : val.rate > 25 ? 'text-amber-600' : 'text-red-600'
                    const bgColor = val.rate > 50 ? 'bg-green-50 border-green-200' : val.rate > 25 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
                    return (
                      <div key={key} className={`rounded-lg border p-3 ${bgColor}`}>
                        <p className={`text-xl font-bold ${rateColor}`}>{val.rate}%</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-xs text-gray-500 truncate">{from}</span>
                          <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-500 truncate">{to}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ── Lead Quality ── */}
            <SectionCard title="Lead Quality" icon={Target}>
              {/* Score gauge */}
              <div className="flex items-center justify-center mb-6">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                    <circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke={getScoreColor(scores.avg_score || 0)}
                      strokeWidth="8"
                      strokeDasharray={`${((scores.avg_score || 0) / 100) * 264} 264`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-gray-900">{scores.avg_score ?? '—'}</span>
                    <span className="text-xs text-gray-400">Avg Score</span>
                  </div>
                </div>
              </div>

              {/* Segment bars */}
              <div className="space-y-3">
                {segmentConfig.map((seg, i) => {
                  const count = scores.segments?.[seg.key] || 0
                  const pct = scores.segment_percentages?.[seg.key] || 0
                  const rangeMax = i === 0 ? 100 : (segmentConfig[i - 1]?.min_score ?? 100) - 1
                  const rangeLabel = `${seg.min_score}\u2013${rangeMax}`
                  return (
                    <div key={seg.key}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                          <span className="text-sm font-medium text-gray-700">{seg.label}</span>
                          <span className="text-xs text-gray-400">({rangeLabel})</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">{count} <span className="font-normal text-gray-400 text-xs">({pct}%)</span></span>
                      </div>
                      <div className="h-2 rounded-full" style={{ backgroundColor: `${seg.color}20` }}>
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: seg.color }} />
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Histogram */}
              {scores.histogram && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Score Distribution</h4>
                  <div className="h-5 flex rounded-full overflow-hidden mb-4">
                    {([
                      { key: '0-20', color: 'bg-blue-400' },
                      { key: '21-40', color: 'bg-sky-400' },
                      { key: '41-60', color: 'bg-yellow-400' },
                      { key: '61-80', color: 'bg-amber-400' },
                      { key: '81-100', color: 'bg-red-400' },
                    ]).map((b) => {
                      const count = (scores.histogram[b.key] as number) || 0
                      const total = Object.values(scores.histogram as Record<string, number>).reduce((a, c) => a + c, 0)
                      const pct = total > 0 ? (count / total) * 100 : 0
                      return pct > 0 ? (
                        <div key={b.key} className={`${b.color} transition-all duration-500`} style={{ width: `${pct}%` }} title={`${b.key}: ${count} leads (${Math.round(pct)}%)`} />
                      ) : null
                    })}
                  </div>
                  <div className="space-y-1.5">
                    {([
                      { key: '0-20', color: 'bg-blue-400', label: '0 – 20', desc: 'Very Cold' },
                      { key: '21-40', color: 'bg-sky-400', label: '21 – 40', desc: 'Cold' },
                      { key: '41-60', color: 'bg-yellow-400', label: '41 – 60', desc: 'Warm' },
                      { key: '61-80', color: 'bg-amber-400', label: '61 – 80', desc: 'Hot' },
                      { key: '81-100', color: 'bg-red-400', label: '81 – 100', desc: 'Very Hot' },
                    ]).map((b) => {
                      const count = (scores.histogram[b.key] as number) || 0
                      return (
                        <div key={b.key} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-sm ${b.color}`} />
                            <span className="text-xs text-gray-600">{b.label}</span>
                            <span className="text-xs text-gray-400">{b.desc}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-800">{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </SectionCard>

            {/* ── Activity ── */}
            <SectionCard title="Activity Breakdown" icon={TrendingUp}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
                  <CheckSquare size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{activity.total_activities || 0}</p>
                  <p className="text-xs text-gray-500">Total activities this period</p>
                </div>
              </div>

              {/* Activity by type with bars */}
              {activity.by_action && Object.keys(activity.by_action).length > 0 ? (
                <div className="space-y-3 mb-5">
                  {Object.entries(activity.by_action)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .map(([action, count]) => {
                      const maxA = Math.max(...Object.values(activity.by_action as Record<string, number>).map(Number), 1)
                      const pct = ((count as number) / maxA) * 100
                      const iconMap: Record<string, any> = {
                        call: Phone, email: Mail, note: CheckSquare,
                        meeting: Users, task: CheckSquare,
                      }
                      const ActionIcon = iconMap[action] || CheckSquare
                      return (
                        <div key={action} className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <ActionIcon size={14} className="text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-700 capitalize">{action.replace(/_/g, ' ')}</span>
                              <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full">
                              <div className="h-full bg-emerald-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No activity data in this period</p>
              )}

              {/* Daily heatmap */}
              {activity.by_date && Object.keys(activity.by_date).length > 0 && (
                <div className="pt-5 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Daily Activity</h4>
                  {/* Mobile: simple list view */}
                  <div className="sm:hidden space-y-1.5">
                    {Object.entries(activity.by_date).slice(-14).map(([date, count]) => {
                      const maxD = Math.max(...Object.values(activity.by_date as Record<string, number>).slice(-14).map(Number), 1)
                      const intensity = maxD > 0 ? (count as number) / maxD : 0
                      const isToday = date === new Date().toISOString().slice(0, 10)
                      const dayLabel = fmtWeekday(date + 'T00:00:00')
                      const dateLabel = fmtDayMonth(date + 'T00:00:00')
                      const dotColor = (count as number) === 0 ? 'bg-gray-200' :
                                       intensity > 0.75 ? 'bg-emerald-500' :
                                       intensity > 0.5 ? 'bg-emerald-400' :
                                       intensity > 0.25 ? 'bg-emerald-300' : 'bg-emerald-200'
                      return (
                        <div
                          key={date}
                          className={`flex items-center justify-between py-1.5 px-2 rounded-md ${isToday ? 'bg-indigo-50 ring-1 ring-indigo-200' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${dotColor} flex-shrink-0`} />
                            <span className="text-sm text-gray-700">{dayLabel}, {dateLabel}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                        </div>
                      )
                    })}
                  </div>
                  {/* Desktop: 7-column heatmap grid */}
                  <div className="hidden sm:grid grid-cols-7 gap-1.5">
                    {Object.entries(activity.by_date).slice(-14).map(([date, count]) => {
                      const maxD = Math.max(...Object.values(activity.by_date as Record<string, number>).slice(-14).map(Number), 1)
                      const intensity = maxD > 0 ? (count as number) / maxD : 0
                      const isToday = date === new Date().toISOString().slice(0, 10)
                      const dayLabel = fmtWeekday(date + 'T00:00:00')
                      const dateLabel = fmtDayMonth(date + 'T00:00:00')
                      const bg = (count as number) === 0 ? 'bg-gray-50' :
                                 intensity > 0.75 ? 'bg-emerald-500' :
                                 intensity > 0.5 ? 'bg-emerald-400' :
                                 intensity > 0.25 ? 'bg-emerald-300' : 'bg-emerald-200'
                      return (
                        <div
                          key={date}
                          className={`rounded-lg p-2 text-center ${bg} ${isToday ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}`}
                          title={`${date}: ${count} activities`}
                        >
                          <p className={`text-[10px] font-medium ${(count as number) > 0 && intensity > 0.5 ? 'text-white' : 'text-gray-500'}`}>{dayLabel}</p>
                          <p className={`text-lg font-bold ${(count as number) > 0 && intensity > 0.5 ? 'text-white' : 'text-gray-800'}`}>{count as number}</p>
                          <p className={`text-[9px] ${(count as number) > 0 && intensity > 0.5 ? 'text-white/70' : 'text-gray-400'}`}>{dateLabel}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </SectionCard>

            {/* ── Lead Sources ── */}
            <SectionCard title="Lead Sources" icon={Users}>
              {sources.by_source && Object.keys(sources.by_source).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(sources.by_source)
                    .sort(([, a]: any, [, b]: any) => b.count - a.count)
                    .map(([source, data]: [string, any], i: number) => {
                      const maxS = Math.max(...Object.values(sources.by_source as Record<string, any>).map((s: any) => s.count), 1)
                      const pct = (data.count / maxS) * 100
                      const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500']
                      const barColor = colors[i % colors.length]
                      return (
                        <div key={source}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${barColor}`} />
                              <span className="text-sm font-medium text-gray-800 capitalize">{source}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-gray-500">{data.count} leads</span>
                              <span className={`font-semibold ${data.conversion_rate > 30 ? 'text-green-600' : data.conversion_rate > 15 ? 'text-amber-600' : 'text-gray-600'}`}>
                                {data.conversion_rate}%
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full">
                            <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                            {data.avg_score != null && <span>Avg score: {data.avg_score}</span>}
                            {data.avg_deal_value != null && <span>Avg deal: ₹{data.avg_deal_value.toLocaleString()}</span>}
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Users size={20} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No source data yet</p>
                  <p className="text-xs text-gray-400 mt-1">Add a source when creating leads to see attribution</p>
                </div>
              )}
            </SectionCard>

            {/* ── Revenue ── */}
            <SectionCard title="Revenue" icon={DollarSign}>
              {/* Revenue stat row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Total Revenue', value: `₹${(revenue.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-purple-500' },
                  { label: 'Deals Won', value: revenue.deal_count || 0, icon: CheckSquare, color: 'bg-emerald-500' },
                  { label: 'Avg Deal Size', value: revenue.avg_deal_size ? `₹${revenue.avg_deal_size.toLocaleString()}` : '—', icon: TrendingUp, color: 'bg-blue-500' },
                ].map((item) => (
                  <div key={item.label} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center mx-auto mb-2`}>
                      <item.icon size={16} className="text-white" />
                    </div>
                    <p className="text-lg font-bold text-gray-900">{item.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* Monthly trend */}
              {revenue.by_month && Object.keys(revenue.by_month).length > 0 ? (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Monthly Revenue</h4>
                  <div className="space-y-2.5">
                    {Object.entries(revenue.by_month).map(([month, val], i, arr) => {
                      const maxR = Math.max(...Object.values(revenue.by_month as Record<string, number>).map(Number), 1)
                      const pct = ((val as number) / maxR) * 100
                      const prev = i > 0 ? (arr[i - 1][1] as number) : null
                      const delta = prev != null && prev > 0 ? Math.round(((val as number) - prev) / prev * 100) : null
                      return (
                        <div key={month}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-600">{month}</span>
                            <div className="flex items-center gap-2">
                              {delta != null && (
                                <span className={`text-xs font-medium flex items-center gap-0.5 ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                  {delta > 0 ? <ArrowUpRight size={12} /> : delta < 0 ? <ArrowDownRight size={12} /> : <Minus size={12} />}
                                  {Math.abs(delta)}%
                                </span>
                              )}
                              <span className="text-sm font-semibold text-gray-900">₹{(val as number).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full">
                            <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <DollarSign size={20} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No revenue data yet</p>
                  <p className="text-xs text-gray-400 mt-1">Mark deals as won with a deal value to track revenue</p>
                </div>
              )}
            </SectionCard>
          </div>

          {/* ── VoIP Call Costs — admin/manager only ── */}
          {isAdminOrManager && (() => {
            const v = (voipData?.data || {}) as any
            const summary = v.summary || {}
            const byAgent: any[] = v.by_agent || []
            const byTeam: any[] = v.by_team || []
            const timeline: any[] = v.timeline || []
            const maxTimelineTotal = Math.max(...timeline.map((t: any) => t.total), 1)

            // Sort agents
            const sortedAgents = [...byAgent].sort((a, b) => {
              const valA = a[agentSort.col] ?? 0
              const valB = b[agentSort.col] ?? 0
              return agentSort.asc ? valA - valB : valB - valA
            })

            const toggleSort = (col: string) => {
              setAgentSort((prev) => prev.col === col ? { col, asc: !prev.asc } : { col, asc: false })
            }

            const sortIcon = (col: string) => {
              if (agentSort.col !== col) return null
              return agentSort.asc ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            }

            return (
              <div className="bg-white rounded-xl border border-gray-200">
                {/* Collapsible header */}
                <button
                  onClick={() => setVoipOpen(!voipOpen)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <div className="flex items-center gap-2">
                    <PhoneCall size={18} className="text-teal-500" />
                    <h2 className="text-lg font-semibold text-gray-900">VoIP Costs</h2>
                  </div>
                  {voipOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                </button>

                {voipOpen && (
                  <div className="px-6 pb-6 space-y-6">
                    {/* Period selector */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <span className="text-sm text-gray-500">Period:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {VOIP_PERIODS.map((p) => (
                          <button
                            key={p.value}
                            onClick={() => setVoipPeriod(p.value)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                              voipPeriod === p.value
                                ? 'bg-teal-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {voipLoading ? (
                      <LoadingState message="Loading cost data..." />
                    ) : (
                      <>
                        {/* Summary cards */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                          <StatCard icon={DollarSign} label="Grand Total" value={fmtINR(summary.grand_total || 0)} color="bg-teal-500" />
                          <StatCard icon={DollarSign} label="Subtotal" value={fmtINR(summary.grand_subtotal || 0)} color="bg-cyan-500" />
                          <StatCard icon={DollarSign} label="Tax" value={fmtINR(summary.grand_tax || 0)} color="bg-sky-500" />
                          <StatCard icon={Phone} label="Billable Calls" value={`${summary.billable_calls || 0} / ${summary.total_calls || 0}`} color="bg-blue-500" />
                          <StatCard icon={TrendingUp} label="Avg Cost / Call" value={fmtINR(summary.avg_cost_per_call || 0)} color="bg-indigo-500" />
                        </div>

                        {/* Agent breakdown */}
                        {byAgent.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">By Agent</h3>
                            {/* Mobile card view */}
                            <div className="md:hidden space-y-3">
                              {sortedAgents.map((a: any) => {
                                const mins = Math.floor((a.billed_seconds || 0) / 60)
                                const secs = (a.billed_seconds || 0) % 60
                                return (
                                  <div key={a.user_id} className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm font-bold text-gray-900 mb-3">{a.user_name}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Calls</p>
                                        <p className="text-sm font-semibold text-gray-800">{a.calls}</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Billed Time</p>
                                        <p className="text-sm font-semibold text-gray-800">{mins}m {secs}s</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total Cost</p>
                                        <p className="text-sm font-bold text-gray-900">{fmtINR(a.total)}</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Avg / Call</p>
                                        <p className="text-sm font-semibold text-gray-800">{fmtINR(a.avg_cost)}</p>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                            {/* Desktop table view */}
                            <div className="hidden md:block overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                                    <th className="pb-2 font-medium">Agent</th>
                                    <th className="pb-2 font-medium text-right cursor-pointer select-none" onClick={() => toggleSort('calls')}>
                                      <span className="inline-flex items-center gap-0.5">Calls {sortIcon('calls')}</span>
                                    </th>
                                    <th className="pb-2 font-medium text-right cursor-pointer select-none" onClick={() => toggleSort('billed_seconds')}>
                                      <span className="inline-flex items-center gap-0.5">Billed Time {sortIcon('billed_seconds')}</span>
                                    </th>
                                    <th className="pb-2 font-medium text-right cursor-pointer select-none" onClick={() => toggleSort('total')}>
                                      <span className="inline-flex items-center gap-0.5">Total {sortIcon('total')}</span>
                                    </th>
                                    <th className="pb-2 font-medium text-right cursor-pointer select-none" onClick={() => toggleSort('avg_cost')}>
                                      <span className="inline-flex items-center gap-0.5">Avg / Call {sortIcon('avg_cost')}</span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedAgents.map((a: any) => {
                                    const mins = Math.floor((a.billed_seconds || 0) / 60)
                                    const secs = (a.billed_seconds || 0) % 60
                                    return (
                                      <tr key={a.user_id} className="border-b border-gray-50 hover:bg-gray-50">
                                        <td className="py-2.5 text-gray-700 font-medium">{a.user_name}</td>
                                        <td className="py-2.5 text-gray-600 text-right">{a.calls}</td>
                                        <td className="py-2.5 text-gray-600 text-right">{mins}m {secs}s</td>
                                        <td className="py-2.5 text-gray-900 font-semibold text-right">{fmtINR(a.total)}</td>
                                        <td className="py-2.5 text-gray-600 text-right">{fmtINR(a.avg_cost)}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Team breakdown */}
                        {byTeam.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">By Team</h3>
                            {/* Mobile card view */}
                            <div className="md:hidden space-y-3">
                              {byTeam.map((t: any) => (
                                <div key={t.team_id} className="bg-gray-50 rounded-lg p-4">
                                  <p className="text-sm font-bold text-gray-900 mb-3">{t.team_name}</p>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Calls</p>
                                      <p className="text-sm font-semibold text-gray-800">{t.calls}</p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total Cost</p>
                                      <p className="text-sm font-bold text-gray-900">{fmtINR(t.total)}</p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Avg / Call</p>
                                      <p className="text-sm font-semibold text-gray-800">{fmtINR(t.avg_cost)}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Desktop table view */}
                            <div className="hidden md:block overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                                    <th className="pb-2 font-medium">Team</th>
                                    <th className="pb-2 font-medium text-right">Calls</th>
                                    <th className="pb-2 font-medium text-right">Total</th>
                                    <th className="pb-2 font-medium text-right">Avg / Call</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {byTeam.map((t: any) => (
                                    <tr key={t.team_id} className="border-b border-gray-50 hover:bg-gray-50">
                                      <td className="py-2.5 text-gray-700 font-medium">{t.team_name}</td>
                                      <td className="py-2.5 text-gray-600 text-right">{t.calls}</td>
                                      <td className="py-2.5 text-gray-900 font-semibold text-right">{fmtINR(t.total)}</td>
                                      <td className="py-2.5 text-gray-600 text-right">{fmtINR(t.avg_cost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Daily spend timeline — CSS bar chart */}
                        {timeline.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Daily Spend</h3>
                            <div className="flex items-end gap-1.5" style={{ height: '160px' }}>
                              {timeline.map((t: any) => {
                                const pct = maxTimelineTotal > 0 ? (t.total / maxTimelineTotal) * 100 : 0
                                const dateStr = t.period || ''
                                const shortDate = dateStr.slice(5) // MM-DD
                                return (
                                  <div key={dateStr} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                                    <div
                                      className="w-full bg-teal-400 rounded-t-sm transition-all duration-300 hover:bg-teal-500"
                                      style={{ height: `${Math.max(pct, 2)}%` }}
                                      title={`${dateStr}: ${fmtINR(t.total)} (${t.calls} calls)`}
                                    />
                                    <span className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">{shortDate}</span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {/* Empty state */}
                        {!byAgent.length && !byTeam.length && !timeline.length && (
                          <div className="flex flex-col items-center justify-center py-10 text-center">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                              <PhoneCall size={20} className="text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-500">No VoIP cost data yet</p>
                            <p className="text-xs text-gray-400 mt-1">Make some calls to see cost breakdowns here</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}
